import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { AuthUser } from '../services/types';
import { apiService } from '../services/api';
import {
  API_UNAUTHORIZED_EVENT,
  clearApiSession,
} from '../services/sessionToken';

// ===== 인증 상태 인터페이스 =====
interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// ===== Context 값 인터페이스 =====
interface AuthContextValue extends AuthState {
  workerLogin: (name: string, pin: string) => Promise<boolean>;
  adminLogin: (loginId: string, password: string) => Promise<boolean>;
  logout: () => void;
}

// localStorage 키
const AUTH_STORAGE_KEY = 'workfit_auth';

// Context 생성
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ===== AuthProvider 컴포넌트 =====
export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // 마운트 시 localStorage에서 기존 세션 복원
  useEffect(() => {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      if (stored) {
        const user: AuthUser = JSON.parse(stored);
        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        setAuthState((prev) => ({ ...prev, isLoading: false }));
      }
    } catch {
      // 파싱 실패 시 세션 제거
      localStorage.removeItem(AUTH_STORAGE_KEY);
      clearApiSession();
      setAuthState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  // 취업자 로그인: apiService 호출
  const workerLogin = useCallback(async (name: string, pin: string): Promise<boolean> => {
    try {
      const worker = await apiService.workerLogin(name.trim(), pin);
      const user: AuthUser = {
        type: 'worker',
        id: worker.worker_id,
        name: worker.name,
      };

      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
      });

      return true;
    } catch (err) {
      console.error('취업자 로그인 API 실패:', err);
      throw err;
    }
  }, []);

  // 관리자 로그인: apiService 호출
  const adminLogin = useCallback(async (loginId: string, password: string): Promise<boolean> => {
    try {
      const admin = await apiService.adminLogin(loginId.trim(), password);
      if (!admin) return false;

      const user: AuthUser = {
        type: 'admin',
        id: admin.admin_id,
        name: admin.name,
        mustChangePassword: admin.must_change_password === true || String(admin.must_change_password).toLowerCase() === 'true',
      };

      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
      });

      return true;
    } catch (err) {
      console.error('관리자 로그인 API 실패:', err);
      throw err;
    }
  }, []);

  // 로그아웃
  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    clearApiSession();
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => logout();
    window.addEventListener(API_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(API_UNAUTHORIZED_EVENT, handleUnauthorized);
  }, [logout]);

  const value: AuthContextValue = {
    ...authState,
    workerLogin,
    adminLogin,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ===== useAuth 커스텀 훅 =====
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth는 AuthProvider 내부에서 사용해야 합니다.');
  }
  return context;
}

export default AuthContext;
